import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getEmailTemplates, createEmailTemplate, updateEmailTemplate, deleteEmailTemplate } from "../../interfaces/emailTemplate";

type EmailModel = {
  _id: string;
  name?: string;
  description?: string;
  html?: string;
  text?: string;
  type?: string;
  replyRate?: number;
  lang?: "en" | "fr";
};

export default function EmailTemplates() {
  const navigate = useNavigate();
  const [language, setLanguage] = useState<"en" | "fr">("en");
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState<EmailModel[]>([]);
  const [search, setSearch] = useState("");

  // CRUD UI state
  const [showAdd, setShowAdd] = useState(false);
  const [newTemplate, setNewTemplate] = useState<{ name: string; description?: string; text: string }>({
    name: "",
    description: "",
    text: ""
  });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<{ name?: string; description?: string; text?: string }>({});
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const filteredTemplates = useMemo(() => {
    const q = search.trim().toLowerCase();
    const onlyEmail = templates
      .filter((t) => (t.type ? t.type !== "sms" : true))
      .filter((t) => !!(t.html || t.text));
    if (!q) return onlyEmail;
    return onlyEmail.filter((t) =>
      [t.name, t.description].filter(Boolean).some((v: any) => String(v).toLowerCase().includes(q))
    );
  }, [templates, search]);

  // Fetch backend templates
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const list: any[] = await getEmailTemplates();
        if (cancelled) return;
        if (Array.isArray(list)) setTemplates(list as any);
      } catch (err) {
        console.warn("Failed to fetch templates", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // CRUD handlers
  const onCreateTemplate = async () => {
    if (!newTemplate.name?.trim() || !newTemplate.text?.trim()) return;
    try {
      setLoading(true);
      const payload: any = {
        name: newTemplate.name.trim(),
        description: (newTemplate.description || "").trim() || undefined,
        text: newTemplate.text,
        type: "email",
        lang: language,
      };
      const created = await createEmailTemplate(payload);
      setTemplates((prev) => [created, ...prev]);
      setNewTemplate({ name: "", description: "", text: "" });
      setShowAdd(false);
    } catch (err) {
      console.warn("Failed to create template", err);
    } finally {
      setLoading(false);
    }
  };

  const startEditTemplate = (t: EmailModel) => {
    setEditingId(t._id);
    setEditDraft({ name: t.name || "", description: t.description || "", text: t.text || t.html || "" });
  };

  const cancelEditTemplate = () => {
    setEditingId(null);
    setEditDraft({});
  };

  const onUpdateTemplate = async () => {
    if (!editingId) return;
    const updates: any = {
      ...(editDraft.name !== undefined ? { name: editDraft.name } : {}),
      ...(editDraft.description !== undefined ? { description: editDraft.description } : {}),
      ...(editDraft.text !== undefined ? { text: editDraft.text } : {}),
    };
    try {
      setLoading(true);
      const updated = await updateEmailTemplate(editingId, updates);
      setTemplates((prev) => prev.map((x) => (x._id === editingId ? updated : x)));
      cancelEditTemplate();
    } catch (err) {
      console.warn("Failed to update template", err);
    } finally {
      setLoading(false);
    }
  };

  const onDeleteTemplate = async (id: string) => {
    try {
      setLoading(true);
      await deleteEmailTemplate(id);
      setTemplates((prev) => prev.filter((x) => x._id !== id));
      if (editingId === id) cancelEditTemplate();
      if (confirmDeleteId === id) setConfirmDeleteId(null);
    } catch (err) {
      console.warn("Failed to delete template", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-canvas font-sans">
      {/* Header */}
      <div className="border-b border-border">
        <div className="max-w-5xl mx-auto px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <button
                className="text-ink-muted hover:text-ink text-xs transition-colors duration-150"
                onClick={() => navigate(-1)}
              >
                ← Back
              </button>
              <div>
                <h1 className="text-sm font-medium text-ink">Email Templates</h1>
                <p className="text-xs text-ink-muted mt-1">Manage your email templates</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-5">
        <div className="bg-surface border border-border rounded-2xl">
          <div className="px-5 py-4 border-b border-border">
            <div>
              <div className="text-xs font-medium text-ink">Templates</div>
              <div className="text-xs text-ink-muted mt-1">Browse, create, edit, and delete your templates</div>
            </div>
          </div>

          <div className="p-4">
            {/* Controls */}
            <div className="flex items-center justify-between gap-2 mb-3">
              <input
                className="w-full px-3 py-2 text-xs border border-border rounded-lg bg-surface text-ink placeholder-ink-faint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:border-accent transition-colors duration-150"
                placeholder="Search templates..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <div className="flex items-center gap-2">
                <button
                  className="text-xs px-2 py-1 border border-border rounded-lg hover:bg-surface-sunken transition-colors duration-150"
                  onClick={() => setShowAdd((s) => !s)}
                >
                  {showAdd ? "Close" : "New"}
                </button>
                <div className="flex border border-border rounded-lg overflow-hidden">
                  <button
                    className={`px-3 py-1 text-xs font-medium transition-colors duration-150 ${
                      language === "en"
                        ? "bg-ink text-ink-inverse"
                        : "bg-surface text-ink-muted hover:bg-surface-sunken"
                    }`}
                    onClick={() => setLanguage("en")}
                  >
                    EN
                  </button>
                  <button
                    className={`px-3 py-1 text-xs font-medium transition-colors duration-150 ${
                      language === "fr"
                        ? "bg-ink text-ink-inverse"
                        : "bg-surface text-ink-muted hover:bg-surface-sunken"
                    }`}
                    onClick={() => setLanguage("fr")}
                  >
                    FR
                  </button>
                </div>
              </div>
            </div>

            {showAdd && (
              <div className="mt-2 p-3 border border-border rounded-lg mb-3">
                <input
                  className="w-full mb-2 px-2 py-1 text-xs border border-border rounded-lg bg-surface text-ink"
                  placeholder="Template name"
                  value={newTemplate.name}
                  onChange={(e) => setNewTemplate((p) => ({ ...p, name: e.target.value }))}
                />
                <input
                  className="w-full mb-2 px-2 py-1 text-xs border border-border rounded-lg bg-surface text-ink"
                  placeholder="Description (optional)"
                  value={newTemplate.description}
                  onChange={(e) => setNewTemplate((p) => ({ ...p, description: e.target.value }))}
                />
                <textarea
                  className="w-full h-24 mb-2 px-2 py-1 text-xs border border-border rounded-lg bg-surface text-ink"
                  placeholder="Email body..."
                  value={newTemplate.text}
                  onChange={(e) => setNewTemplate((p) => ({ ...p, text: e.target.value }))}
                />
                <div className="flex justify-end gap-2">
                  <button
                    className="px-3 h-8 text-xs border border-border rounded-lg bg-surface text-ink-muted hover:bg-surface-sunken transition-colors duration-150"
                    onClick={() => setShowAdd(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="px-3 h-8 text-xs rounded-lg bg-ink text-ink-inverse hover:bg-ink/90 disabled:opacity-50 transition-colors duration-150"
                    onClick={onCreateTemplate}
                    disabled={!newTemplate.name.trim() || !newTemplate.text.trim() || loading}
                  >
                    Save
                  </button>
                </div>
              </div>
            )}

            {/* Loading state */}
            {loading && (
              <div className="p-4 text-xs text-ink-faint text-center">Loading...</div>
            )}

            {/* List */}
            <div className="max-h-[70vh] overflow-auto">
              {!loading && !filteredTemplates.length && (
                <div className="p-4 text-xs text-ink-faint">No templates found.</div>
              )}
              {filteredTemplates.map((t, index) => {
                const isEditing = editingId === t._id;
                return (
                  <div key={t._id} className={`p-4 ${index < filteredTemplates.length - 1 ? "border-b border-border" : ""}`}>
                    {!isEditing ? (
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-medium text-ink mb-1">{t.name || "Untitled"}</div>
                          {t.description && <div className="text-xs text-ink-muted line-clamp-2 mb-2">{t.description}</div>}
                          {t.replyRate && (
                            <div className="text-xs text-success">Reply rate: {t.replyRate}%</div>
                          )}
                          <div className="text-xs text-ink-faint mt-2 line-clamp-3 whitespace-pre-wrap">{t.text || t.html}</div>
                        </div>
                        <div className="ml-3 flex gap-2">
                          <button
                            className="text-xs px-2 py-1 border border-border rounded-lg hover:bg-surface-sunken transition-colors duration-150"
                            onClick={(e) => {
                              e.stopPropagation();
                              startEditTemplate(t);
                            }}
                          >
                            Edit
                          </button>
                          <button
                            className="text-xs px-2 py-1 border border-border rounded-lg hover:bg-danger-subtle text-danger transition-colors duration-150"
                            onClick={(e) => {
                              e.stopPropagation();
                              setConfirmDeleteId(t._id);
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="p-2 border border-border rounded-lg">
                        <input
                          className="w-full mb-2 px-2 py-1 text-xs border border-border rounded-lg bg-surface text-ink"
                          placeholder="Template name"
                          value={editDraft.name || ""}
                          onChange={(e) => setEditDraft((p) => ({ ...p, name: e.target.value }))}
                        />
                        <input
                          className="w-full mb-2 px-2 py-1 text-xs border border-border rounded-lg bg-surface text-ink"
                          placeholder="Description (optional)"
                          value={editDraft.description || ""}
                          onChange={(e) => setEditDraft((p) => ({ ...p, description: e.target.value }))}
                        />
                        <textarea
                          className="w-full h-24 mb-2 px-2 py-1 text-xs border border-border rounded-lg bg-surface text-ink"
                          placeholder="Email body..."
                          value={editDraft.text || ""}
                          onChange={(e) => setEditDraft((p) => ({ ...p, text: e.target.value }))}
                        />
                        <div className="flex justify-end gap-2">
                          <button
                            className="px-3 h-8 text-xs border border-border rounded-lg bg-surface text-ink-muted hover:bg-surface-sunken transition-colors duration-150"
                            onClick={cancelEditTemplate}
                          >
                            Cancel
                          </button>
                          <button
                            className="px-3 h-8 text-xs rounded-lg bg-ink text-ink-inverse hover:bg-ink/90 disabled:opacity-50 transition-colors duration-150"
                            onClick={onUpdateTemplate}
                            disabled={!editDraft.name?.trim() || !editDraft.text?.trim() || loading}
                          >
                            Save
                          </button>
                        </div>
                      </div>
                    )}
                    {confirmDeleteId === t._id && (
                      <div className="mt-2 flex gap-2">
                        <button
                          className="text-xs px-2 py-1 border border-border rounded-lg hover:bg-surface-sunken transition-colors duration-150"
                          onClick={() => setConfirmDeleteId(null)}
                        >
                          Cancel
                        </button>
                        <button
                          className="text-xs px-2 py-1 border border-danger rounded-lg text-danger hover:bg-danger-subtle transition-colors duration-150"
                          onClick={() => onDeleteTemplate(t._id)}
                        >
                          Confirm
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}